import React, { createContext, useContext } from "react";
import { DeepPartial } from "./@types/DeepPartial";
import { mergeDeep } from "./@utils/mergeDeep";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface CustomTheme {}
export interface Theme {
  palette: {
    intentions: {
      primary: {
        light: string;
        main: string;
        dark: string;
        contrastText: string;
      };
      secondary: {
        light: string;
        main: string;
        dark: string;
        contrastText: string;
      };
      accent: {
        light: string;
        main: string;
        dark: string;
        contrastText: string;
      };
      success: {
        light: string;
        main: string;
        dark: string;
        contrastText: string;
      };
      info: {
        light: string;
        main: string;
        dark: string;
        contrastText: string;
      };
      warning: {
        light: string;
        main: string;
        dark: string;
        contrastText: string;
      };
      error: {
        light: string;
        main: string;
        dark: string;
        contrastText: string;
      };
    };
    gray: {
      50: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      900: string;
    };
    text: {
      primary: string;
      secondary: string;
      disabled: string;
      hint: string;
    };
    background: {
      paper: string;
      overlay: string;
      default: string;
    };
    action: {
      active: string;
      hover: string;
      hoverOpacity: number;
      selected: string;
      disabled: string;
      disabledBackground: string;
      readOnly: string;
      readOnlyBackground: string;
    };
  };
  spacing: number;
  breakpoints: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
    xl: number;
  };
  typography: {
    h1: {
      fontFamily: string;
      fontWeight: number;
      fontSize: number;
      lineHeight: number;
      letterSpacing: string;
      fontSmoothing: string;
    };
    h2: {
      fontFamily: string;
      fontWeight: number;
      fontSize: number;
      lineHeight: number;
      letterSpacing: string;
      fontSmoothing: string;
    };
    h3: {
      fontFamily: string;
      fontWeight: number;
      fontSize: number;
      lineHeight: number;
      letterSpacing: string;
      fontSmoothing: string;
    };
    h4: {
      fontFamily: string;
      fontWeight: number;
      fontSize: number;
      lineHeight: number;
      letterSpacing: string;
      fontSmoothing: string;
    };
    h5: {
      fontFamily: string;
      fontWeight: number;
      fontSize: number;
      lineHeight: number;
      letterSpacing: string;
      fontSmoothing: string;
    };
    body: {
      fontFamily: string;
      fontWeight: number;
      fontSize: number;
      lineHeight: number;
      letterSpacing: string;
      fontSmoothing: string;
    };
    body2: {
      fontFamily: string;
      fontWeight: number;
      fontSize: number;
      lineHeight: number;
      letterSpacing: string;
      fontSmoothing: string;
    };
    button: {
      fontFamily: string;
      fontWeight: number;
      fontSize: number;
      lineHeight: number;
      letterSpacing: string;
      fontSmoothing: string;
    };
    caption: {
      fontFamily: string;
      fontWeight: number;
      fontSize: number;
      lineHeight: number;
      letterSpacing: string;
      fontSmoothing: string;
    };
    /**
     * カレンダーの日付のフォント設定
     * Themeの移行に伴い導入したものであり、ユーザーが利用することは想定していない。
     */
    INTERNALS_DO_NOT_USE_calendarDay: {
      fontFamily: string;
      fontWeight: number;
      fontSize: number;
      lineHeight: number;
      letterSpacing: string;
      fontSmoothing: string;
    };
    /**
     * カレンダーが日付に子要素を持っている場合のフォント設定
     * Themeの移行に伴い導入したものであり、ユーザーが利用することは想定していない。
     */
    INTERNALS_DO_NOT_USE_calendarDay_hasChildren: {
      fontFamily: string;
      fontWeight: number;
      fontSize: number;
      lineHeight: number;
      letterSpacing: string;
      fontSmoothing: string;
    };
  };
  transitions: {
    easing: {
      easeInOut: string;
      easeOut: string;
      easeIn: string;
      sharp: string;
    };
    duration: {
      shortest: string;
      shorter: string;
      short: string;
      standard: string;
      complex: string;
      enteringScreen: string;
      leavingScreen: string;
    };
  };
  shadows: string[];
  zIndex: {
    mobileStepper: number;
    speedDial: number;
    appBar: number;
    drawer: number;
    modal: number;
    snackbar: number;
    tooltip: number;
  };
}

export const defaultTheme: Theme = {
  palette: {
    intentions: {
      primary: {
        light: "#FFD8BD",
        main: "#F46500",
        dark: "#E63D00",
        contrastText: "#FFF",
      },
      secondary: {
        light: "#B1DADE",
        main: "#419DA7",
        dark: "#125E6E",
        contrastText: "#FFF",
      },
      accent: {
        light: "#C5D5E8",
        main: "#0C58B1",
        dark: "#033894",
        contrastText: "#FFF",
      },
      success: {
        light: "#c8e6c9",
        main: "#66bb6a",
        dark: "#338a3e",
        contrastText: "#FFF",
      },
      info: {
        light: "#bbdefb",
        main: "#42a5f5",
        dark: "#1976d2",
        contrastText: "#FFF",
      },
      warning: {
        light: "#ffecb3",
        main: "#ffca28",
        dark: "#ffa000",
        contrastText: "#FFF",
      },
      error: {
        light: "#FFCCD2",
        main: "#D0021B",
        dark: "#A30014",
        contrastText: "#FFF",
      },
    },
    gray: {
      50: "#fafafa",
      100: "#f5f5f5",
      200: "#eeeeee",
      300: "#e0e0e0",
      400: "#bdbdbd",
      500: "#9e9e9e",
      600: "#757575",
      700: "#616161",
      800: "#424242",
      900: "#212121",
    },
    text: {
      primary: "rgba(0, 0, 0, 0.87)",
      secondary: "rgba(0, 0, 0, 0.54)",
      disabled: "rgba(0, 0, 0, 0.38)",
      hint: "rgba(0, 0, 0, 0.38)",
    },
    background: {
      paper: "#fff",
      overlay: "#424242",
      default: "#fafafa",
    },
    action: {
      active: "rgba(0, 0, 0, 0.54)",
      hover: "rgba(0, 0, 0, 0.08)",
      hoverOpacity: 0.08,
      selected: "rgba(0, 0, 0, 0.14)",
      disabled: "rgba(0, 0, 0, 0.26)",
      disabledBackground: "rgba(0, 0, 0, 0.12)",
      readOnly: "rgba(0, 0, 0, 0.12)",
      readOnlyBackground: "rgba(0, 0, 0, 0.04)",
    },
  },
  spacing: 7,
  breakpoints: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
  },
  typography: {
    h1: {
      fontFamily: `"Noto Sans", "Noto Sans JP", sans-serif`,
      fontWeight: 300,
      fontSize: 32,
      lineHeight: 1,
      letterSpacing: "-0.01562em",
      fontSmoothing: "antialiased",
    },
    h2: {
      fontFamily: `"Noto Sans", "Noto Sans JP", sans-serif`,
      fontWeight: 300,
      fontSize: 24,
      lineHeight: 1,
      letterSpacing: "-0.00833em",
      fontSmoothing: "antialiased",
    },
    h3: {
      fontFamily: `"Noto Sans", "Noto Sans JP", sans-serif`,
      fontWeight: 400,
      fontSize: 21,
      lineHeight: 1.04,
      letterSpacing: "0em",
      fontSmoothing: "antialiased",
    },
    h4: {
      fontFamily: `"Noto Sans", "Noto Sans JP", sans-serif`,
      fontWeight: 400,
      fontSize: 18,
      lineHeight: 1.17,
      letterSpacing: "0.00735em",
      fontSmoothing: "antialiased",
    },
    // 新しいテーマへの移行用に配置、h4と同じ値を設定している。
    h5: {
      fontFamily: `"Noto Sans", "Noto Sans JP", sans-serif`,
      fontWeight: 400,
      fontSize: 18,
      lineHeight: 1.17,
      letterSpacing: "0.00735em",
      fontSmoothing: "antialiased",
    },
    body: {
      fontFamily: `"Noto Sans", "Noto Sans JP", sans-serif`,
      fontWeight: 400,
      fontSize: 14,
      lineHeight: 1.5,
      letterSpacing: "0.00938em",
      fontSmoothing: "antialiased",
    },
    // 新しいテーマへの移行用に配置、bodyと同じ値を設定している。
    body2: {
      fontFamily: `"Noto Sans", "Noto Sans JP", sans-serif`,
      fontWeight: 400,
      fontSize: 14,
      lineHeight: 1.5,
      letterSpacing: "0.00938em",
      fontSmoothing: "antialiased",
    },
    button: {
      fontFamily: `"Noto Sans", "Noto Sans JP", sans-serif`,
      fontWeight: 500,
      fontSize: 16,
      lineHeight: 1.75,
      letterSpacing: "0.02857em",
      fontSmoothing: "antialiased",
    },
    caption: {
      fontFamily: `"Noto Sans", "Noto Sans JP", sans-serif`,
      fontWeight: 400,
      fontSize: 12,
      lineHeight: 1.66,
      letterSpacing: "0.03333em",
      fontSmoothing: "antialiased",
    },
    INTERNALS_DO_NOT_USE_calendarDay: {
      fontFamily: `"Noto Sans", "Noto Sans JP", sans-serif`,
      fontWeight: 300,
      fontSize: 24,
      lineHeight: 1,
      letterSpacing: "-0.00833em",
      fontSmoothing: "antialiased",
    },
    INTERNALS_DO_NOT_USE_calendarDay_hasChildren: {
      fontFamily: `"Noto Sans", "Noto Sans JP", sans-serif`,
      fontWeight: 300,
      fontSize: 18,
      lineHeight: 1,
      letterSpacing: "-0.00833em",
      fontSmoothing: "antialiased",
    },
  },
  transitions: {
    easing: {
      easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
      easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
      easeIn: "cubic-bezier(0.4, 0, 1, 1)",
      sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
    },
    duration: {
      shortest: "150ms",
      shorter: "200ms",
      short: "250ms",
      standard: "300ms",
      complex: "375ms",
      enteringScreen: "225ms",
      leavingScreen: "195ms",
    },
  },
  shadows: [
    "none",
    "0px 1px 3px 0px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 2px 1px -1px rgba(0,0,0,0.12)",
    "0px 1px 5px 0px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 3px 1px -2px rgba(0,0,0,0.12)",
    "0px 1px 8px 0px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 3px 3px -2px rgba(0,0,0,0.12)",
    "0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)",
    "0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)",
    "0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)",
    "0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)",
    "0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)",
    "0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)",
    "0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)",
    "0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)",
    "0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)",
    "0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)",
    "0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)",
    "0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)",
    "0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)",
    "0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)",
    "0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)",
    "0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)",
    "0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)",
    "0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)",
    "0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)",
    "0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)",
    "0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)",
  ],
  zIndex: {
    mobileStepper: 1000,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
};

/**
 * 将来的に移行予定のテーマ
 */
export const futureTheme = {
  typography: {
    h1: {
      fontFamily: `"Noto Sans", "Noto Sans JP", sans-serif`,
      fontWeight: 500,
      fontSize: 40,
      lineHeight: 1.6,
      letterSpacing: "-0.8px",
      fontSmoothing: "antialiased",
    },
    h2: {
      fontFamily: `"Noto Sans", "Noto Sans JP", sans-serif`,
      fontWeight: 500,
      fontSize: 32,
      lineHeight: 1.5,
      letterSpacing: "0px",
      fontSmoothing: "antialiased",
    },
    h3: {
      fontFamily: `"Noto Sans", "Noto Sans JP", sans-serif`,
      fontWeight: 500,
      fontSize: 28,
      lineHeight: 1.4285714285,
      letterSpacing: "0.1px",
      fontSmoothing: "antialiased",
    },
    h4: {
      fontFamily: `"Noto Sans", "Noto Sans JP", sans-serif`,
      fontWeight: 500,
      fontSize: 24,
      lineHeight: 1.33333333333,
      letterSpacing: "0.3px",
      fontSmoothing: "antialiased",
    },
    h5: {
      fontFamily: `"Noto Sans", "Noto Sans JP", sans-serif`,
      fontWeight: 500,
      fontSize: 20,
      lineHeight: 1.6,
      letterSpacing: "0.3px",
      fontSmoothing: "antialiased",
    },
    body: {
      fontFamily: `"Noto Sans", "Noto Sans JP", sans-serif`,
      fontWeight: 400,
      fontSize: 16,
      lineHeight: 1.5,
      letterSpacing: "0px",
      fontKeerning: "none",
      fontSmoothing: "antialiased",
    },
    body2: {
      fontFamily: `"Noto Sans", "Noto Sans JP", sans-serif`,
      fontWeight: 400,
      fontSize: 14,
      lineHeight: 1.42857142857,
      letterSpacing: "0px",
      fontKeerning: "none",
      fontSmoothing: "antialiased",
    },
    button: {
      fontFamily: `"Noto Sans", "Noto Sans JP", sans-serif`,
      fontWeight: 500,
      fontSize: 16,
      lineHeight: 1.75,
      letterSpacing: "0.02857em",
      fontSmoothing: "antialiased",
    },
    caption: {
      fontFamily: `"Noto Sans", "Noto Sans JP", sans-serif`,
      fontWeight: 400,
      fontSize: 12,
      lineHeight: 1.66,
      letterSpacing: "0.03333em",
      fontSmoothing: "antialiased",
    },
    INTERNALS_DO_NOT_USE_calendarDay: {
      fontFamily: `"Noto Sans", "Noto Sans JP", sans-serif`,
      fontWeight: 300,
      fontSize: 24,
      lineHeight: 1,
      letterSpacing: "-0.00833em",
      fontSmoothing: "antialiased",
    },
    INTERNALS_DO_NOT_USE_calendarDay_hasChildren: {
      fontFamily: `"Noto Sans", "Noto Sans JP", sans-serif`,
      fontWeight: 300,
      fontSize: 18,
      lineHeight: 1,
      letterSpacing: "-0.00833em",
      fontSmoothing: "antialiased",
    },
  },
};

type PickInternsionsKey<B> = B extends {
  palette: {
    intentions: infer D;
  };
}
  ? keyof D
  : never;

export type Color =
  | keyof Theme["palette"]["intentions"]
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  | PickInternsionsKey<CustomTheme>;

type PickTypographyKey<B> = B extends {
  typography: infer D;
}
  ? keyof D
  : never;

export type TypographyVariant =
  | keyof Theme["typography"]
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  | PickTypographyKey<CustomTheme>;

export interface PartialColorProps {
  /**
   * 色を指定します。
   *
   * @default default
   */
  color?: Color | "default";
}

const ThemeContext = createContext(defaultTheme as Theme & CustomTheme);

export interface ThemeProviderProps {
  theme: DeepPartial<Theme & CustomTheme>;
  children?: React.ReactNode;
}

export const ThemeProvider = ({ theme, children }: ThemeProviderProps) => {
  return (
    <ThemeContext.Provider value={mergeDeep(defaultTheme, theme)}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const theme = useContext(ThemeContext);
  return theme;
};
